import { defineStore } from 'pinia'
import bannerType from '@/data/bannerType.js'
import SearchService from '@/services/search.service.js'
import SearchActionService from '@/services/searchAction.service.js'
import { unwrapApiData } from '@/services/apiEnvelope'

const defaultSearchState = () => ({
    displayData: false,
    selectedPart: '',
    selectedPartIndex: '',
    // Draft quote identity keeps repeated searched part rows independent.
    selectedQuoteId: null,
    quoteData: [],
    salesData: [],
    inventoryData: {},
    openSalesOrdersData: {},
    openPurchaseOrdersData: {},
    statsNumData: [],
    salesNotes: [],
    partsArray: [],
    bpa_parts: [],
    bpa_minimums: {},
    gsa_parts: [],
    gsa_minimums: {},
    vendors: {},
    stats: {
        avg_cost: 0,
        avg_price: 0,
        avg_quote: 0,
        avg_margin: 0,
        last_quote: 0,
        won: 0,
        quotes_tot: 0,
        quotes_tot_qty: 0,
        sales_tot: 0,
        sales_tot_qty: 0,
    },
    partData: {
        googleSearch: {},
        boeing: {},
        aeroPerformance: {},
        aeroFastener: {},
        textron: {},
        textronDefense: {},
        partsBase: {},
    },
    statsData: {},
    totals: {
        vendors: 0,
        sales: 0,
        quotes: 0,
        ils: 0,
        markePlace: 0,
        googleSearch: 0,
        parts: 0,
        substitutes: 0,
        stats: 0,
    },
    tables: {
        greenwoodPoncaInventory: [],
        googleSearch: [],
        sales: [],
        quotes: [],
        repairOrders: [],
        openSalesOrders: [],
        openPurchaseOrders: [],
        dbParts: [],
        dbCapParts: [],
        ils: [],
        partsBase: [],
        markePlace: [],
        substitutes: [],
        boeingAviall: [],
        aeroPerformance: [],
        aeroFastener: [],
        textron: [],
        textronDefense: [],
        stats: [],
        notes: [],
    },
    partsSearched: {},
    fuzzy_parts_matches: {},
    banner: {
        message: '',
        show: false,
        onConfirm: null,
        toggle: false,
        type: bannerType.PERSISTENT,
    },
    searchActionId: null,
    customer: '',
    quote_type: '',
    customer_ref: '',
    search_finished: false,
    quote_finished: false,
    source: '',
    subdomain: '',
    is_nsn: false,
    part_and_sub_list: [],
    master_subs_dict: {},
    quantities: [],
    quantities_search_action_id: null,
})

const getInitialState = () => ({
    search: defaultSearchState(),
    ajax_searching: 0,
    parts_array: [],
    part_selected: '',
    part_index: 0,
    match_so_array: [],
    so_part_selected: '',
    so_index: 0,
    search_parts_permissions: [],
    pulse_pn_count: null,
    pulse_pn_total: null,
    pulse_process: null,
    pulse_sub_count: null,
    pulse_sub_total: null,
})

const mergeTotals = (totals) => ({
    vendors: 0,
    sales: 0,
    quotes: 0,
    ils: 0,
    markePlace: 0,
    googleSearch: 0,
    parts: 0,
    substitutes: 0,
    stats: 0,
    ...(totals || {}),
})

export const useSearchStore = defineStore('search', {
    state: () => ({
        ...getInitialState(),
    }),
    getters: {
        getDisplayData: (state) => state.search.displayData,
        getStats: (state) => state.search.statsData,
        getTotals: (state) => state.search.totals,
        getTables: (state) => state.search.tables,
        getTotalBoingAviall: (state) => {
            const boeingAviall = state.search.tables.boeingAviall
            return boeingAviall && boeingAviall.length > 0 ? boeingAviall.length : 0
        },
        getTotalAeroPerformance: (state) => {
            const aeroPerformance = state.search.tables.aeroPerformance
            return aeroPerformance && aeroPerformance.length > 0 ? aeroPerformance.length : 0
        },
        getTotalAeroFastener: (state) => {
            const aeroFastener = state.search.tables.aeroFastener
            return aeroFastener && aeroFastener.length > 0 ? aeroFastener.length : 0
        },
        getTotalTextron: (state) => {
            const textron = state.search.tables.textron
            return textron && textron.length > 0 ? textron.length : 0
        },
        getTotalTextronDefense: (state) => {
            const textronDefense = state.search.tables.textronDefense
            return textronDefense && textronDefense.length > 0 ? textronDefense.length : 0
        },
        calculateTotals() {
            return {
                vendors: this.getTotalVendors ? this.getTotalVendors : 0,
                sales: this.getTotalSales ? this.getTotalSales : 0,
                quotes: this.getTotalQuotes ? this.getTotalQuotes : 0,
                ils: this.getTotalIls ? this.getTotalIls : 0,
                markePlace: this.getTotalMarketplaceParts ? this.getTotalMarketplaceParts : 0,
                googleSearch: this.getTotalGoogleSearch ? this.getTotalGoogleSearch : 0,
                parts: this.getTotalParts ? this.getTotalParts : 0,
                substitutes: this.getTotalSubstitutes ? this.getTotalSubstitutes : 0,
                stats: this.getTotalStats ? this.getTotalStats : 0,
                notes: this.getTotalNotes ? this.getTotalNotes : 0,
            }
        },
        getTotalVendors() {
            return (
                this.getTotalBoingAviall +
                this.getTotalAeroPerformance +
                this.getTotalAeroFastener +
                this.getTotalTextron +
                this.getTotalTextronDefense
            )
        },
        getTotalSales: (state) => {
            const sales = state.search.tables.sales
            return sales && sales.length > 0 ? sales.length : 0
        },
        getTotalQuotes: (state) => {
            const quotes = state.search.tables.quotes
            return quotes && quotes.length > 0 ? quotes.length : 0
        },
        getTotalIls: (state) => {
            const ils = state.search.tables.ils
            return ils && ils.length > 0 ? ils.length : 0
        },
        getTotalMarketplaceParts: (state) => {
            const marketplaceParts = state.search.tables.marketplaceParts
            return marketplaceParts && marketplaceParts.length > 0 ? marketplaceParts.length : 0
        },
        getTotalGoogleSearch: (state) => {
            const googleSearch = state.search.tables.googleSearch
            return googleSearch && googleSearch.length > 0 ? googleSearch.length : 0
        },
        getTotalParts: (state) => {
            const parts = state.search.tables.partsBase
            return parts && parts.length > 0 ? parts.length : 0
        },
        getTotalSubstitutes: (state) => {
            const substitutes = state.search.tables.substitutes
            return substitutes && substitutes.length > 0 ? substitutes.length : 0
        },
        getTotalStats: (state) => {
            const stats = state.search.statsData
            return stats && Object.keys(stats).length > 0 ? Object.keys(stats).length : 0
        },
        getTotalNotes: (state) => {
            const notes = state.search.tables.notes
            return notes && notes.length > 0 ? notes.length : 0
        },
        getPartDataGoogleSearch: (state) => state.search.partData.googleSearch,
        getPartDataBoeing: (state) => state.search.partData.boeing,
        getPartDataAeroPerformance: (state) => state.search.partData.aeroPerformance,
        getPartDataAeroFastener: (state) => state.search.partData.aeroFastener,
        getPartDataTextron: (state) => state.search.partData.textron,
        getPartDataTextronDefense: (state) => state.search.partData.textronDefense,
        getPartsBase: (state) => state.search.partData.partsBase,
        getSelectedPart: (state) => {
            const selectedPart = state.search.selectedPart
            if (!selectedPart) {
                return state.search.partsArray[0]
            }
            return state.search.selectedPart
        },
        getSelectedPartIndex: (state) => {
            const selectedPartIndex = state.search.selectedPartIndex
            if (!selectedPartIndex) {
                return 0
            }
            return state.search.selectedPartIndex
        },
        getSelectedQuoteId: (state) => state.search.selectedQuoteId,
        getQuoteData: (state) => state.search.quoteData,
        getSalesData: (state) => state.search.salesData,
        getInventoryData: (state) => state.search.inventoryData,
        getOpenSalesOrdersData: (state) => state.search.openSalesOrdersData,
        getOpenPurchaseOrdersData: (state) => state.search.openPurchaseOrdersData,
        getStatsNumData: (state) => state.search.statsNumData,
        getSalesNotes: (state) => state.search.salesNotes,
        getVendors: (state) => state.search.vendors,
        getFuzzyPartsMatches: (state) => state.search.fuzzy_parts_matches,
        getPartsArray: (state) => state.search.partsArray,
        getPartData: (state) => state.search.partData,
        getPartsSearched: (state) => state.search.partsSearched,
        getBanner: (state) => state.search.banner,
        getSearchActionId: (state) => state.search.searchActionId,
        getCustomer: (state) => state.search.customer,
        getQuoteType: (state) => state.search.quote_type,
        getCustomerRef: (state) => state.search.customer_ref,
        getBpaParts: (state) => state.search.bpa_parts,
        getBpaMinimums: (state) => state.search.bpa_minimums,
        getGsaParts: (state) => state.search.gsa_parts,
        getGsaMinimums: (state) => state.search.gsa_minimums,
        getSearchFinished: (state) => state.search.search_finished,
        getQuoteFinished: (state) => state.search.quote_finished,
        getSource: (state) => state.search.source,
        getSubdomain: (state) => state.search.subdomain,
        getIsNsn: (state) => state.search.is_nsn,
        getPartAndSubList: (state) => state.search.part_and_sub_list,
        getMasterSubsDict: (state) => state.search.master_subs_dict,
        getQuantities: (state) => state.search.quantities,
        getQuantitiesSearchActionId: (state) => state.search.quantities_search_action_id,
    },
    actions: {
        resetSearchStore() {
            this.parts_array = []
            this.part_selected = ''
            this.part_index = 0
            this.match_so_array = []
            this.so_part_selected = ''
            this.so_index = 0
            this.ajax_searching = 0
            this.search.selectedQuoteId = null
        },
        reset_search_store() {
            this.resetSearchStore()
        },
        set_pulse_pn_count(val) {
            this.pulse_pn_count = val
        },
        set_pulse_pn_total(val) {
            this.pulse_pn_total = val
        },
        set_pulse_process(val) {
            this.pulse_process = val
        },
        set_pulse_sub_count(val) {
            this.pulse_sub_count = val
        },
        set_pulse_sub_total(val) {
            this.pulse_sub_total = val
        },
        set_parts_array(partsArray) {
            this.parts_array = partsArray
        },
        set_part_selected(partSelected) {
            this.part_selected = partSelected
        },
        set_part_index(partIndex) {
            this.part_index = partIndex
        },
        set_match_so_array(matchSoArray) {
            this.match_so_array = matchSoArray
        },
        set_so_part_selected(soPartSelected) {
            this.so_part_selected = soPartSelected
        },
        set_so_index(soIndex) {
            this.so_index = soIndex
        },
        set_ajax_searching(isSearching) {
            if (isSearching) {
                this.ajax_searching += 1
            } else if (this.ajax_searching > 0) {
                this.ajax_searching -= 1
            }
        },
        set_search_parts_permissions(permissions) {
            this.search_parts_permissions = permissions || []
        },
        setDisplayData(value) {
            this.search.displayData = value
        },
        setSearchActionId(searchActionId) {
            this.search.searchActionId = searchActionId || null
        },
        clearSearchActionId() {
            this.search.searchActionId = null
        },
        setSearchQuantities({ searchActionId, quantities }) {
            this.search.quantities_search_action_id = searchActionId || null
            this.search.quantities = Array.isArray(quantities) ? quantities : []
        },
        clearSearchQuantities() {
            this.search.quantities_search_action_id = null
            this.search.quantities = []
        },
        setCustomer(customer) {
            this.search.customer = customer || ''
        },
        setCustomerRef(customerRef) {
            this.search.customer_ref = customerRef || ''
        },
        async updateSearchActionCustomerReference({ id, customer, customer_ref }) {
            try {
                const response = await SearchActionService.updateCustomerReference({
                    id,
                    customer,
                    customer_ref,
                })
                const data = unwrapApiData(response, response)
                if (data?.customer !== undefined) {
                    this.setCustomer(data.customer)
                }
                if (data?.customer_ref !== undefined) {
                    this.setCustomerRef(data.customer_ref)
                }
                return data
            } catch (error) {
                console.error('Failed to update customer/reference', error)
                throw error
            }
        },
        async updateSearchActionParts({
            search_action_id,
            part_number_list,
            new_parts_list,
            changed_parts_dict,
        }) {
            try {
                const response = await SearchActionService.updateParts({
                    search_action_id,
                    part_number_list,
                    new_parts_list,
                    changed_parts_dict,
                })
                return unwrapApiData(response, response)
            } catch (error) {
                console.error('Failed to update search action parts', error)
                throw error
            }
        },
        setStats(statsObject) {
            this.search.stats = statsObject
        },
        setTableGreenwoodPoncaInventory(data) {
            this.search.tables.greenwoodPoncaInventory = data
        },
        setTableGoogleSearch(data) {
            this.search.tables.googleSearch = data
        },
        setTableSales(data) {
            this.search.tables.sales = data
        },
        setTableQuotes(data) {
            this.search.tables.quotes = data
        },
        setTableRepairOrders(data) {
            this.search.tables.repairOrders = data
        },
        setTableOpenSalesOrders(data) {
            this.search.tables.openSalesOrders = data
        },
        setTableOpenPurchaseOrders(data) {
            this.search.tables.openPurchaseOrders = data
        },
        setTableDbParts(data) {
            this.search.tables.dbParts = data
        },
        setTableDbCapParts(data) {
            this.search.tables.dbCapParts = data
        },
        setTableIls(data) {
            this.search.tables.ils = data
        },
        setTablePartsBase(data) {
            this.search.tables.partsBase = data
        },
        appendTablePartsBase(data) {
            this.search.tables.partsBase = (this.search.tables.partsBase || []).concat(data || [])
        },
        setTableBoeingAviall(data) {
            this.search.tables.boeingAviall = data
        },
        setTableAeroPerformance(data) {
            this.search.tables.aeroPerformance = data
        },
        setTableAeroFastener(data) {
            this.search.tables.aeroFastener = data
        },
        setTableTextron(data) {
            this.search.tables.textron = data
        },
        setTableTextronDefense(data) {
            this.search.tables.textronDefense = data
        },
        setTablesetTableDbCapParts(data) {
            this.search.tables.dbCapParts = data
        },
        setTableStats(data) {
            this.search.tables.stats = data
        },
        setTotals(totals) {
            this.search.totals = mergeTotals(totals)
        },
        setTableNotes(data) {
            this.search.tables.notes = data
        },
        setPartData({ part, data, vendor }) {
            if (vendor === 'Google Search') {
                this.search.partData.googleSearch[part] = data
            }
            if (vendor === 'Boeing') {
                this.search.partData.boeing[part] = data
            }
            if (vendor === 'Aero Performance') {
                this.search.partData.aeroPerformance[part] = data
            }
            if (vendor === 'Aero Fastener') {
                this.search.partData.aeroFastener[part] = data
            }
            if (vendor === 'Textron') {
                this.search.partData.textron[part] = data
            }
            if (vendor === 'Textron Defense') {
                this.search.partData.textronDefense[part] = data
            }
            if (vendor === 'Parts Base') {
                this.search.partData.partsBase[part] = data
            }
        },
        clearAllPartData() {
            this.search.partData.googleSearch = {}
            this.search.partData.boeing = {}
            this.search.partData.aeroPerformance = {}
            this.search.partData.aeroFastener = {}
            this.search.partData.textron = {}
            this.search.partData.textronDefense = {}
            this.search.partData.partsBase = {}
        },
        cleanAllTables() {
            this.search.tables.greenwoodPoncaInventory = []
            this.search.tables.googleSearch = []
            this.search.tables.sales = []
            this.search.tables.quotes = []
            this.search.tables.repairOrders = []
            this.search.tables.openSalesOrders = []
            this.search.tables.openPurchaseOrders = []
            this.search.tables.dbParts = []
            this.search.tables.dbCapParts = []
            this.search.tables.ils = []
            this.search.tables.partsBase = []
            this.search.tables.markePlace = []
            this.search.tables.substitutes = []
            this.search.tables.boeingAviall = []
            this.search.tables.aeroPerformance = []
            this.search.tables.aeroFastener = []
            this.search.tables.textron = []
            this.search.tables.textronDefense = []
            this.search.tables.stats = []
            this.search.tables.notes = []
        },
        clearAllTotals() {
            this.search.totals = mergeTotals()
        },
        clearPartData({ part, vendor }) {
            if (vendor === 'Google Search') {
                this.search.partData.googleSearch[part] = []
            }
            if (vendor === 'Boeing') {
                this.search.partData.boeing[part] = []
            }
            if (vendor === 'Aero Performance') {
                this.search.partData.aeroPerformance[part] = []
            }
            if (vendor === 'Aero Fastener') {
                this.search.partData.aeroFastener[part] = []
            }
            if (vendor === 'Textron') {
                this.search.partData.textron[part] = []
            }
            if (vendor === 'Textron Defense') {
                this.search.partData.textronDefense[part] = []
            }
        },
        setPartStatsData(data) {
            this.search.statsData[data.part] = data
        },
        setSelectedPart(part) {
            this.search.selectedPart = part
        },
        setSelectedPartIndex(index) {
            this.search.selectedPartIndex = index
        },
        setSelectedQuoteId(quoteId) {
            this.search.selectedQuoteId = quoteId || null
        },
        setQuoteData(data) {
            this.search.quoteData = data
        },
        setSalesData(data) {
            this.search.salesData = data
        },
        setInventoryData(data) {
            this.search.inventoryData = data || {}
        },
        clearInventoryData() {
            this.search.inventoryData = {}
        },
        setOpenSalesOrdersData(data) {
            this.search.openSalesOrdersData = data || {}
        },
        setOpenPurchaseOrdersData(data) {
            this.search.openPurchaseOrdersData = data || {}
        },
        setStatsNumData(data) {
            this.search.statsNumData = data
        },
        setSalesNotes(data) {
            this.search.salesNotes = data
        },
        clearVendorPart(part) {
            this.search.vendors[part] = []
        },
        clearVendors() {
            this.search.vendors = {}
        },
        clearPartsSearched() {
            this.search.partsSearched = {}
        },
        setVendors({ data, part }) {
            this.search.vendors[part] = data
        },
        setBpaParts(parts) {
            this.search.bpa_parts = parts || []
        },
        setBpaMinimums(map) {
            this.search.bpa_minimums = map || {}
        },
        setGsaParts(parts) {
            this.search.gsa_parts = parts || []
        },
        setGsaMinimums(map) {
            this.search.gsa_minimums = map || {}
        },
        setFuzzyPartsMatches({ data, part }) {
            this.search.fuzzy_parts_matches[part] = data
        },
        setPartsArray(data) {
            this.search.partsArray = data
        },
        appendToVendors({ data, part }) {
            if (!this.search.vendors[part]) {
                this.search.vendors[part] = []
            }
            this.search.vendors[part] = this.search.vendors[part].concat(data || [])
        },
        setPartsSearched({ data, part }) {
            if (!this.search.partsSearched[part]) {
                this.search.partsSearched[part] = []
            }
            const existingParts = this.search.partsSearched[part] || []
            const incomingParts = Array.isArray(data) ? data : []
            this.search.partsSearched[part] = Array.from(
                new Set([...existingParts, ...incomingParts].filter(Boolean))
            )
        },
        setBanner(data) {
            if (!data.show) {
                throw new Error('Banner data must include a show property')
            } else if (!data.message) {
                throw new Error('Banner data must include a message property')
            }
            if (!data.type) {
                data.type = bannerType.DISMISSIBLE
            }
            this.search.banner = data
        },
        setBannerStatus(status) {
            this.search.banner.show = status
        },
        toggleBanner() {
            this.search.banner.toggle = !this.search.banner.toggle
        },
        async loadSearchActionData(searchActionId) {
            const data = await SearchService.fetchSearchActionData(searchActionId)

            if (!data) {
                return null
            }

            this.setSearchActionId(data.id || searchActionId || null)
            this.search.customer = data.customer || ''
            this.search.quote_type = data.quote_type || ''
            this.search.customer_ref = data.customer_ref || ''
            this.search.search_finished = !!data.search_finished
            this.search.quote_finished = !!data.quote_finished
            this.search.source = data.source || ''
            this.search.subdomain = data.subdomain || ''
            this.search.is_nsn = !!data.is_nsn
            this.search.part_and_sub_list = data.part_and_sub_list || []
            this.search.master_subs_dict = data.master_subs_dict || {}

            const parts = data.part_number_list || []
            this.setPartsArray(parts)
            this.set_parts_array(parts)
            this.setSelectedPart(parts[0] || '')
            this.setSelectedPartIndex(0)
            this.set_part_selected(parts[0] || '')
            this.set_part_index(0)

            return data
        },
        async loadSearchActionInventory(searchActionId) {
            const response = await SearchActionService.getInventory(searchActionId)
            const data = unwrapApiData(response, response)
            this.setInventoryData(data?.inventory || {})
            return data
        },
        setTables({ searchDataRaw, site }) {
            const dataFiltered = (searchDataRaw || []).filter((item) => item.site === site)
            const tableSales = dataFiltered && dataFiltered.length > 0 ? dataFiltered[0] : null
            const data =
                tableSales && tableSales.data && tableSales.data.length > 0 ? tableSales.data : []

            if (site === 'Greenwood Ponca Inventory') {
                this.setTableGreenwoodPoncaInventory(data)
            }
            if (site === 'Sales') {
                this.setTableSales(data)
            }
            if (site === 'Quotes') {
                this.setTableQuotes(data)
            }
            if (site === 'Repair Orders') {
                this.setTableRepairOrders(data)
            }
            if (site === 'Open Sales Orders') {
                this.setTableOpenSalesOrders(data)
            }
            if (site === 'Open Purchase Orders') {
                this.setTableOpenPurchaseOrders(data)
            }
            if (site === 'db_parts') {
                this.setTableDbParts(data)
            }
            if (site === 'db_cap_parts') {
                this.setTablesetTableDbCapParts(data)
            }
            if (site === 'ILS') {
                this.setTableIls(data)
            }
            if (site === 'PartsBase') {
                this.setTablePartsBase(data)
            }
            if (site === 'Boeing/Aviall') {
                this.setTableBoeingAviall(data)
            }
            if (site === 'Aero Performance') {
                this.setTableAeroPerformance(data)
            }
            if (site === 'AeroFastener') {
                this.setTableAeroFastener(data)
            }
            if (site === 'Textron') {
                this.setTableTextron(data)
            }
            if (site === 'Textron Defense') {
                this.setTableTextronDefense(data)
            }
        },
        setResetStats() {
            this.search.stats = {
                avg_cost: 0,
                avg_price: 0,
                avg_quote: 0,
                avg_margin: 0,
                last_quote: 0,
                won: 0,
                quotes_tot: 0,
                quotes_tot_qty: 0,
                sales_tot: 0,
                sales_tot_qty: 0,
            }
        },
    },
})
